import { Component, OnInit } from '@angular/core';
import { EmailValidator, FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import Swal from 'sweetalert2';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  EquipmentSummaryDTO,
  EquipmentDetailDTO,
  EquipmentServiceDetailDTO,
  CategorySummaryDTO,
  SubCategorySummaryDTO,
  LocationSummaryDTO,
  IsServiceOkDTO,
  EquipmentBorrowDetailDTO,
} from 'projects/EquipmentRegistration/src/equipment-registration-base/dataTypes';
import { User, Department } from 'BaseDataTypes';
import { dialogInputTitleComponent } from 'src/app/elements/dialog-input-title.component';
import { SaveButtonComponent } from 'src/app/buttons/save-button/save-button.component';
import { DeleteButtonComponent } from 'src/app/buttons/delete-button/delete-button.component';
import { MainService } from 'BaseServices/main-service';
import { UserService } from 'BaseServices/user-service';
import { CommonModule } from '@angular/common';
import { DepartmentService } from '../../../../../TrainingTracking/src/training-tracking-base/Services/department-service';

type SimpleItem = { id: number; name: string; categoryId?: number };
type ColorItem = { id: string; name: string };

@Component({
  selector: 'app-equipment-management',
  standalone: true,
  imports: [
    FormsModule,
    MatCheckboxModule,
    CommonModule,
    dialogInputTitleComponent,
    SaveButtonComponent,
    DeleteButtonComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatNativeDateModule,
    MatInputModule,
    MatSelectModule
  ],
  templateUrl: './equipment-management.component.html',
  styleUrl: './equipment-management.component.css',
})
export class EquipmentManagementComponent implements OnInit {
  isEditMode = false;
  private editId = 0;
  hasBorrowHistory = false;
  hasServiceHistory = false;
  hasEquipmentImage = false;
  isBorrowedOut = false;
  currentBorrowId: number | null = null;
  qrImageSrc: SafeUrl | null = null;
  equipmentImageSrc: SafeUrl | null = null;

  IsServiceOkDTO = IsServiceOkDTO;

  readonly isServiceOkLabel: Record<number, string> = {
    [IsServiceOkDTO.OK]: 'OK',
    [IsServiceOkDTO.IkkeKontrolleret]: 'Ikke kontrolleret',
    [IsServiceOkDTO.Defekt]: 'Defekt',
  };

  get controlStatusText(): string {
    const n = Number(this.equipmentRequest?.isServiceOK ?? IsServiceOkDTO.IkkeKontrolleret);
    return this.isServiceOkLabel[n] ?? '';
  }


  subCategories: SubCategorySummaryDTO[] = [];
  locations: LocationSummaryDTO[] = [];
  responsibleUsers: User[] = [];
  departmentList: Department[] = [];
  categories: CategorySummaryDTO[] = [];

  categoryItems: SimpleItem[] = [];
  subCategoryItems: SimpleItem[] = [];
  locationItems: SimpleItem[] = [];
  responsibleItems: SimpleItem[] = [];
  departmentItems: SimpleItem[] = [];
  filteredSubCategoryItems: SimpleItem[] = [];

  serviceColorItems: ColorItem[] = [
    { id: '#FF9900', name: 'Orange' },
    { id: '#FFFFFF', name: 'Hvid' },
    { id: '#FF0000', name: 'Rød' },
    { id: '#00FF00', name: 'Grøn' },
    { id: '#FFFF00', name: 'Gul' },
    { id: '#9900FF', name: 'Lilla' },
    { id: '#0000FF', name: 'Blå' },
    { id: '#00FFFF', name: 'Cyan' },
    { id: '#FF00FF', name: 'Magenta' },
  ];

  selectedCategoryId = 0;
  selectedSubCategoryId = 0;
  selectedDepartmentId = 0;
  selectedLocationId = 0;
  selectedResponsibleUserId = 0;

  selectedImageName: string | null = null;
  selectedDocumentNames: string[] = [];

  nextControlText = '';
  lastServiceTypeText = '';

  equipmentRequest: EquipmentDetailDTO = {
    id: 0,
    name: '',
    model: '',
    series: '',
    comment: '',
    qrToken: '',
    supplierName: '',
    serviceFrequencyMonths: '',
    serviceFrequencyDelay: 0,
    purchaseDate: new Date(),
    placement: '',
    alarmRecipientEmail: '',
    supplierPhone: '',
    supplierAddress: '',
    bestbefore: new Date(),
    guideLink: '',
    responsibleId: 0,
    createdById: 1,
    isServiceOK: IsServiceOkDTO.IkkeKontrolleret,
    departmentId: 0,
    locationId: 0,
    subCategoryId: 0,
    hasServiceHistory: false,

  };

  equipmentService: EquipmentServiceDetailDTO = {
    id: 0,
    serviceDate: new Date(),
    serviceComment: '',
    serviceBy: '',
    serviceColor: '',
    typeService: false,
    typeCalibration: false,
    typeRepair: false,
    typeMalfunction: false,
    typeVerification: false,
    equipmentId: 0
  };

  equipmentBorrow: EquipmentBorrowDetailDTO = {
    id: 0,
    initials: '',
    borrowedDate: new Date(),
    expectedReturnDate: new Date(),
    returnedDate: undefined,
    equipmentId: 0
  };

  private lookupsLoaded = false;

  constructor(
    private mainService: MainService,
    public userService: UserService,
    public departmentService: DepartmentService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe((pm) => {
      this.editId = Number(pm.get('id') ?? 0);
      this.isEditMode = this.editId > 0;

      if (!this.isEditMode) this.resetForm();
      this.tryLoadEditEquipment();
    });

    this.userService.getUsers().subscribe((users) => {
      this.responsibleUsers = users ?? [];
      this.responsibleItems = this.responsibleUsers.map((u) => ({
        id: u.id,
        initials: u.initials,
        name: u.name,
      }));
      this.tryLoadEditEquipment();
    });

    this.departmentService.getDepartments().subscribe((departments) => {
      this.departmentList = departments ?? [];
      this.departmentItems = this.departmentList.map((d) => ({
        id: d.id,
        name: d.displayName,
      }));
      this.tryLoadEditEquipment();
    });

    this.mainService.getAll<CategorySummaryDTO>('Category').subscribe((list) => {
      this.categories = list ?? [];
      this.categoryItems = this.categories
        .map((c) => ({
          id: c.id,
          name: c.name.trim(),
        }))
        .filter(x => x.id > 0 && x.name.length > 0);

      this.tryLoadEditEquipment();
    });

    this.mainService.getAll<SubCategorySummaryDTO>('SubCategory').subscribe((list) => {
      this.subCategories = list ?? [];
      this.subCategoryItems = this.subCategories
        .map((sc) => ({
          id: sc.id,
          name: sc.name.trim(),
          categoryId: sc.categoryId,
        }))
        .filter(x => x.id > 0 && x.name.length > 0);

      this.filteredSubCategoryItems = [...this.subCategoryItems];

      this.tryLoadEditEquipment();
    });

    this.mainService.getAll<LocationSummaryDTO>('Location').subscribe((list) => {
      this.locations = list ?? [];
      this.locationItems = this.locations.map((l) => ({
        id: l.id,
        name: l.name,
      }));
      this.tryLoadEditEquipment();
    });
  }

  get serviceColorName(): string {
    const id = String(this.equipmentService?.serviceColor ?? '');
    return this.serviceColorItems.find((x) => String(x.id) === id)?.name ?? '';
  }

  get serviceOkText(): string {
    const v = Number(this.equipmentRequest?.isServiceOK ?? IsServiceOkDTO.IkkeKontrolleret);
    if (v === IsServiceOkDTO.OK) return 'OK';
    if (v === IsServiceOkDTO.Defekt) return 'Defekt';
    return 'Ikke kontrolleret';
  }

  private tryLoadEditEquipment(): void {
    if (this.lookupsLoaded) return;

    const ready =
      (this.categories?.length ?? 0) > 0 &&
      (this.subCategories?.length ?? 0) > 0 &&
      (this.locations?.length ?? 0) > 0 &&
      (this.departmentList?.length ?? 0) > 0 &&
      (this.responsibleUsers?.length ?? 0) > 0;

    if (!ready) return;

    this.lookupsLoaded = true;

    if (this.isEditMode && this.editId > 0) {
      this.loadEquipment(this.editId);
    }
  }

  private loadEquipment(id: number): void {
    this.mainService.get<EquipmentDetailDTO>('Equipment', id).subscribe({
      next: (eq) => {
        this.populateForm(eq);
      },
    });
  }

  private date(value: any, fallback: Date | null) {
    if (!value) return fallback;
    const date = new Date(value);
    return isNaN(date.getTime()) ? fallback : date;
  }

  private addMonths(date: Date, months: number): Date {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    if (d.getDate() < day) d.setDate(0);
    return d;
  }

  private parseFrequencyMonths(frequencyText: unknown): number {
    const text = String(frequencyText ?? '').trim();

    if (text.length === 0) return 0;
    const match = text.match(/\d+/);

    if (!match) return 0;

    return Number(match[0]);
  }

  private NextControlDateCal(lastDate: Date | null, freqText: any): void {
    const months = this.parseFrequencyMonths(freqText);
    if (!lastDate || !months || months <= 0) {
      this.nextControlText = '';
      return;
    }
    const next = this.addMonths(lastDate, months);
    this.nextControlText = next.toLocaleDateString('da-DK');
  }

  recalcNextControl(): void {
    const last =
      this.equipmentService?.serviceDate instanceof Date ? this.equipmentService.serviceDate : null;
    this.NextControlDateCal(last, this.equipmentRequest.serviceFrequencyMonths);
  }

  private setEquipmentImage(equipmentImage: any): void {
    if (!equipmentImage) {
      this.equipmentImageSrc = null;
      return;
    }

    if (equipmentImage instanceof Uint8Array) {
      let binary = '';
      for (let i = 0; i < equipmentImage.length; i++) {
        binary += String.fromCharCode(equipmentImage[i]);
      }

      const base64 = btoa(binary);
      this.equipmentImageSrc = this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${base64}`);
      return;
    }

    if (Array.isArray(equipmentImage)) {
      const bytes = new Uint8Array(equipmentImage);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      const base64 = btoa(binary);
      this.equipmentImageSrc = this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${base64}`);
      return;
    }

    const imageText = String(equipmentImage).trim();
    if (!imageText || imageText === 'null' || imageText === 'undefined') {
      this.equipmentImageSrc = null;
      return;
    }

    if (imageText.startsWith('data:image/') || imageText.startsWith('http')) {
      this.equipmentImageSrc = this.sanitizer.bypassSecurityTrustUrl(imageText);
      return;
    }

    this.equipmentImageSrc = this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${imageText}`);
  }

  private setQrImage(qrImage: any): void {
    if (!qrImage) {
      this.qrImageSrc = null;
      return;
    }

    const qrImageText = String(qrImage).trim();
    if (!qrImageText || qrImageText === 'null' || qrImageText === 'undefined') {
      this.qrImageSrc = null;
      return;
    }

    if (qrImageText.startsWith('data:image/') || qrImageText.startsWith('http')) {
      this.qrImageSrc = this.sanitizer.bypassSecurityTrustUrl(qrImageText);
      return;
    }

    if (qrImageText.startsWith('0x') || qrImageText.startsWith('0X')) {
      let hex = qrImageText.slice(2).replace(/\s+/g, '');
      if (hex.length % 2 !== 0) {
        this.qrImageSrc = null;
        return;
      }

      const bytes = new Uint8Array(hex.length / 2);
      for (let i = 0; i < hex.length; i += 2) {
        const b = parseInt(hex.substring(i, i + 2), 16);
        if (Number.isNaN(b)) {
          this.qrImageSrc = null;
          return;
        }
        bytes[i / 2] = b;
      }

      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      const base64 = btoa(binary);
      this.qrImageSrc = this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${base64}`);
      return;
    }

    this.qrImageSrc = this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${qrImageText}`);
  }

  private populateForm(equipment: EquipmentDetailDTO): void {
    this.isBorrowedOut = !!equipment.isBorrowedOut;
    this.currentBorrowId = equipment.currentBorrowId ?? null;

    const id = equipment.id;
    this.qrImageSrc = this.sanitizer.bypassSecurityTrustUrl(`/api/Equipment/${id}/qr-image`);

    this.hasEquipmentImage = !!equipment.equipmentImage;
    this.equipmentImageSrc = this.hasEquipmentImage
      ? this.sanitizer.bypassSecurityTrustUrl(`/api/Equipment/${id}/equipment-image`)
      : null;

    const departmentId = equipment.departmentId ?? 0;
    const locationId = equipment.locationId ?? 0;
    const subCatId = equipment.subCategoryId ?? 0;
    const respId = equipment.responsibleId ?? 0;

    const sub = this.subCategories.find((s) => s.id === subCatId);
    const categoryId = sub?.categoryId ?? 0;

    this.equipmentRequest = {
      ...this.equipmentRequest,
      id,
      name: equipment.name,
      model: equipment.model ?? '',
      series: equipment.series ?? '',
      comment: equipment.comment ?? equipment.usageNotice ?? '',
      supplierName: equipment.supplierName ?? '',
      serviceFrequencyMonths: equipment.serviceFrequencyMonths ?? '',
      serviceFrequencyDelay: equipment.serviceFrequencyDelay ?? 0,
      purchaseDate: this.date(equipment.purchaseDate, new Date()) ?? new Date(),
      bestbefore: this.date(equipment.bestbefore, new Date()) ?? new Date(),
      placement: equipment.placement ?? '',
      alarmRecipientEmail: equipment.alarmRecipientEmail ?? '',
      isServiceOK: equipment.isServiceOK ?? IsServiceOkDTO.IkkeKontrolleret,
      departmentId,
      locationId,
      subCategoryId: subCatId,
      responsibleId: respId,
      createdById: equipment.createdById ?? this.equipmentRequest.createdById,
    };

    this.selectedCategoryId = categoryId;
    this.selectedSubCategoryId = subCatId;
    this.equipmentRequest.subCategoryId = subCatId;
    this.updateSubCategoryItems();

    this.selectedDepartmentId = departmentId;
    this.equipmentRequest.departmentId = departmentId;

    this.selectedLocationId = locationId;
    this.equipmentRequest.locationId = locationId;

    this.selectedResponsibleUserId = respId;
    this.equipmentRequest.responsibleId = respId;

    this.hasServiceHistory = equipment.hasServiceHistory;

    const lastServiceDate = this.date(equipment.lastServiceDate, null);
    this.lastServiceTypeText = equipment.lastServiceType ?? '';

    if (this.hasServiceHistory) {
      this.equipmentService = {
        ...this.equipmentService,
        serviceBy: equipment.lastServicePerformedBy ?? '',
        serviceDate: lastServiceDate ?? new Date(),
        serviceComment: equipment.lastServiceComment ?? '',
        serviceColor: equipment.lastServiceColor ?? '',
        typeCalibration: false,
        typeService: false,
        typeRepair: false,
        typeMalfunction: false,
        typeVerification: false,
      };
      this.NextControlDateCal(this.equipmentService.serviceDate, this.equipmentRequest.serviceFrequencyMonths);
    } else {
      this.lastServiceTypeText = '';
      this.nextControlText = '';
      this.equipmentService = {
        ...this.equipmentService,
        serviceBy: '',
        serviceDate: new Date(),
        serviceComment: '',
        serviceColor: '',
        typeService: false,
        typeCalibration: false,
        typeRepair: false,
        typeVerification: false,
        typeMalfunction: false,
      };
    }

    this.hasBorrowHistory = !!equipment.hasBorrowHistory;

    if (this.hasBorrowHistory) {
      this.equipmentBorrow = {
        ...this.equipmentBorrow,
        borrowedDate: this.date(equipment.lastBorrowDate, null) ?? new Date(),
        initials: equipment.lastBorrowPersonInitials ?? '',
        locationName: equipment.lastBorrowLocationName ?? '',
        expectedReturnDate: this.date(equipment.lastBorrowExpectedReturnDate, null) ?? new Date(),
        borrowedComment: equipment.lastBorrowComment ?? '',
        returnedDate: this.date(equipment.lastReturnDate, null) ?? undefined,
        returnedComment: equipment.lastReturnComment ?? '',
        projectNr: '',
      };
    } else {
      this.equipmentBorrow = {
        ...this.equipmentBorrow,
        initials: '',
        locationName: '',
        borrowedComment: '',
        returnedComment: '',
        projectNr: '',
        borrowedDate: new Date(),
        expectedReturnDate: new Date(),
        returnedDate: undefined,
      };
    }

    if (this.isEditMode) {
      this.equipmentRequest.qrToken = '';
      this.equipmentRequest.guideLink = '';
      this.equipmentRequest.supplierPhone = '';
      this.equipmentRequest.supplierAddress = '';
    }
  }

  private updateSubCategoryItems(): void {
    const selectedCatId = Number(this.selectedCategoryId ?? 0);

    if (!selectedCatId) {
      this.filteredSubCategoryItems = [...this.subCategoryItems];
      return;
    }

    this.filteredSubCategoryItems = this.subCategoryItems.filter(
      x => Number(x.categoryId ?? 0) === selectedCatId
    );
  }


  onCategoryChange = () => {
    this.selectedCategoryId = Number(this.selectedCategoryId ?? 0);
    this.updateSubCategoryItems();

    const selectedSubCategoryId = Number(this.selectedSubCategoryId ?? 0);

    if (selectedSubCategoryId <= 0) {
      this.equipmentRequest.subCategoryId = 0;
      return;
    }

    const selectedStillExists = this.filteredSubCategoryItems.some(
      x => x.id === selectedSubCategoryId
    );

    if (!selectedStillExists) {
      this.selectedSubCategoryId = 0;
      this.equipmentRequest.subCategoryId = 0;
    }
  };


  onSubCategoryChange = () => {
    const subCategoryId = Number(this.selectedSubCategoryId ?? 0);
    this.equipmentRequest.subCategoryId = subCategoryId;

    const selectedSubCategory = this.subCategoryItems.find(
      x => x.id === subCategoryId
    );

    if (!selectedSubCategory) return;

    this.selectedCategoryId = Number(selectedSubCategory.categoryId ?? 0);
    this.updateSubCategoryItems();
  };

  onDepartmentChange = () => {
    this.equipmentRequest.departmentId = Number(this.selectedDepartmentId ?? 0);
  };

  onLocationChange = () => {
    this.equipmentRequest.locationId = Number(this.selectedLocationId ?? 0);
  };

  onResponsibleChange = () => {
    const id = Number(this.selectedResponsibleUserId ?? 0);
    this.equipmentRequest.responsibleId = id;
  };

  onEquipmentImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (!file) {
      this.selectedImageName = null;
      return;
    }

    this.selectedImageName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const base64 = result.includes('base64,')
        ? result.split('base64,')[1]
        : result;

      this.equipmentRequest.equipmentImage = base64;
      this.equipmentImageSrc = this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${base64}`);
    };

    reader.readAsDataURL(file);
  }

  onEquipmentDocumentsSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      this.selectedDocumentNames = [];
      return;
    }
    this.selectedDocumentNames = Array.from(files).map((f) => f.name);
  }

  get controlTypeText(): string {
    const types: string[] = [];

    if (this.equipmentService.typeService) types.push('Service');
    if (this.equipmentService.typeCalibration) types.push('Kalibrering');
    if (this.equipmentService.typeRepair) types.push('Reparation');
    if (this.equipmentService.typeVerification) types.push('Verifikation');
    if (this.equipmentService.typeMalfunction) types.push('Defekt');

    return types.length ? types.join(',') : '';
  }

  get isSaveDisabled(): boolean {
    const eq = this.equipmentRequest;
    if (!eq) return true;

    return (
      this.selectedCategoryId <= 0 ||
      !eq.subCategoryId || eq.subCategoryId <= 0 ||
      !eq.name || eq.name.trim().length === 0 ||
      !eq.supplierName || eq.supplierName.trim().length === 0 ||
      !eq.model || eq.model.trim().length === 0 ||
      !eq.series || eq.series.trim().length === 0 ||
      !eq.purchaseDate || !(eq.purchaseDate instanceof Date) ||
      !eq.bestbefore || !(eq.bestbefore instanceof Date) ||
      !eq.departmentId || eq.departmentId <= 0 ||
      !eq.locationId || eq.locationId <= 0 ||
      !eq.responsibleId || eq.responsibleId <= 0 ||
      !eq.placement || eq.placement.trim().length === 0 ||
      !eq.serviceFrequencyMonths || String(eq.serviceFrequencyMonths).trim().length === 0 ||
      !eq.serviceFrequencyDelay || eq.serviceFrequencyDelay <= 0
    );
  }

  get isControlSaveDisabled(): boolean {
    if (!this.isEditMode || !this.equipmentRequest?.id) return true;

    const s = this.equipmentService;

    const hasType =
      !!s.typeService ||
      !!s.typeCalibration ||
      !!s.typeRepair ||
      !!s.typeVerification ||
      !!s.typeMalfunction;

    return (
      !s.serviceBy || s.serviceBy.trim().length === 0 ||
      !(s.serviceDate instanceof Date) ||
      !hasType
    );
  }

  get isBorrowSaveDisabled(): boolean {
    if (!this.isEditMode || !this.equipmentRequest?.id) return true;
    if (this.isBorrowedOut) return true;

    const b = this.equipmentBorrow;
    return (
      !b.initials || b.initials.trim().length === 0 ||
      !(b.expectedReturnDate instanceof Date)
    );
  }

  performControl(): void {
    if (this.isControlSaveDisabled) return;

    const equipmentId = Number(this.equipmentRequest.id);

    const payload: any = {
      EquipmentId: equipmentId,
      ServiceDate: this.equipmentService.serviceDate,
      ServiceBy: (this.equipmentService.serviceBy ?? '').trim(),
      ServiceComment: (this.equipmentService.serviceComment ?? '').trim(),
      ServiceColor: this.equipmentService.serviceColor ?? null,

      TypeService: !!this.equipmentService.typeService,
      TypeCalibration: !!this.equipmentService.typeCalibration,
      TypeRepair: !!this.equipmentService.typeRepair,
      TypeVerification: !!this.equipmentService.typeVerification,
      TypeMalfunction: !!this.equipmentService.typeMalfunction,
    };

    this.mainService.add<any, any>('EquipmentService', payload).subscribe({
      next: () => {
        Swal.fire({
          title: 'Gemt',
          text: 'Kontrol er gemt.',
          icon: 'success',
          confirmButtonText: 'Ok',
        });
        this.router.navigate(['../../søgudstyr'], { relativeTo: this.activatedRoute });
        this.loadEquipment(equipmentId);

      },
    });
  }

  performBorrow(): void {
    if (this.isBorrowSaveDisabled) return;

    const equipmentId = Number(this.equipmentRequest.id);

    const payload: any = {
      EquipmentId: Number(this.equipmentRequest.id),  
      Initials: (this.equipmentBorrow.initials ?? '').trim(),
      BorrowedDate: new Date(),
      ExpectedReturnDate: this.equipmentBorrow.expectedReturnDate,
      BorrowedComment: (this.equipmentBorrow.borrowedComment ?? '').trim(),
      ProjectNr: (this.equipmentBorrow.projectNr ?? '').trim(),
      LocationName: this.equipmentBorrow.locationName ?? ''
    };

    this.mainService.add<any, any>('Equipmentborrows', payload).subscribe({
      next: () => {
        Swal.fire({
          title: 'Gemt',
          text: 'Udstyret er udlånt.',
          icon: 'success',
          confirmButtonText: 'Ok',
        }).then(() => {
          this.router.navigate(['../../søgudstyr'], { relativeTo: this.activatedRoute });
        });

        this.loadEquipment(equipmentId);
      },
      error: (err) => {
        console.error(err);
        Swal.fire({
          title: 'Fejl',
          text: 'Kunne ikke gemme udlån.',
          icon: 'error',
          confirmButtonText: 'Ok',
        });
      }
    });
  }

  performReturn(): void {
    if (!this.isEditMode || !this.equipmentRequest?.id) return;

    const borrowId = Number(this.currentBorrowId ?? 0);
    if (!borrowId) {
      Swal.fire({
        title: 'Fejl',
        text: 'Kan ikke returnere: mangler currentBorrowId.',
        icon: 'error',
        confirmButtonText: 'Ok',
      }).then(() => {
        this.router.navigate(['../../søgudstyr'], { relativeTo: this.activatedRoute });
      });
      return;
    }

    if (!(this.equipmentBorrow.returnedDate instanceof Date)) {
      Swal.fire({
        title: 'Fejl',
        text: 'Vælg en returdato.',
        icon: 'error',
        confirmButtonText: 'Ok',
      });
      return;
    }

    Swal.fire({
      title: 'Returner udstyr?',
      text: 'Bekræft at udstyret er returneret.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, returner',
      cancelButtonText: 'Annuller',
    }).then((res) => {
      if (!res.isConfirmed) return;

      const payload: any = {
        Id: borrowId,
        EquipmentId: Number(this.equipmentRequest.id),

        ReturnedDate: this.equipmentBorrow.returnedDate,
        ReturnedComment: (this.equipmentBorrow.returnedComment ?? '').trim(),

        Initials: (this.equipmentBorrow.initials ?? '').trim(),
        BorrowedDate: this.equipmentBorrow.borrowedDate ?? null,
        ExpectedReturnDate: this.equipmentBorrow.expectedReturnDate ?? null,
        BorrowedComment: (this.equipmentBorrow.borrowedComment ?? '').trim(),
        ProjectNr: (this.equipmentBorrow.projectNr ?? '').trim(),
        LocationName: (this.equipmentBorrow.locationName ?? '').trim(),
      };

      this.mainService.update<any, any>('Equipmentborrows', payload).subscribe({
        next: () => {
          Swal.fire({
            title: 'Returneret',
            text: 'Udstyret er returneret.',
            icon: 'success',
            confirmButtonText: 'Ok',
          }).then(() => {
            this.router.navigate(['../../søgudstyr'], { relativeTo: this.activatedRoute });
          });
        },
        error: (err) => {
          console.error(err);
          Swal.fire({
            title: 'Fejl',
            text: 'Kunne ikke returnere udstyret.',
            icon: 'error',
            confirmButtonText: 'Ok',
          });
        },
      });
    });
  }


  saveChanges() {
    if (this.isSaveDisabled) return;

    if (!this.equipmentRequest.subCategoryId || this.equipmentRequest.subCategoryId <= 0) {
      this.equipmentRequest.subCategoryId = this.selectedSubCategoryId;
    }
    if (!this.equipmentRequest.departmentId || this.equipmentRequest.departmentId <= 0) {
      this.equipmentRequest.departmentId = this.selectedDepartmentId;
    }
    if (!this.equipmentRequest.locationId || this.equipmentRequest.locationId <= 0) {
      this.equipmentRequest.locationId = this.selectedLocationId;
    }

    const responsibleId = Number(this.selectedResponsibleUserId ?? 0);
    if (!responsibleId || responsibleId <= 0) {
      Swal.fire({
        title: 'Fejl',
        text: 'Du skal vælge en ansvarlig bruger.',
        icon: 'error',
        confirmButtonText: 'Ok',
      });
      return;
    }

    this.equipmentRequest.responsibleId = responsibleId;

    if (!(this.equipmentRequest.purchaseDate instanceof Date)) {
      this.equipmentRequest.purchaseDate = new Date(this.equipmentRequest.purchaseDate as string);
    }
    if (!(this.equipmentRequest.bestbefore instanceof Date)) {
      this.equipmentRequest.bestbefore = new Date(this.equipmentRequest.bestbefore as string);
    }

    const dto: any = { ...this.equipmentRequest };
    dto.Id = Number(this.equipmentRequest.id); 
    delete dto.id; 

if (this.isEditMode) {
  this.mainService.update<any, any>('Equipment', dto).subscribe({
    next: (saved) => {
      Swal.fire({
        title: 'Gemt',
        text: 'Udstyret er opdateret.',
        icon: 'success',
        confirmButtonText: 'Ok',
      }).then(() => {
        this.router.navigate(['../../søgudstyr'], { relativeTo: this.activatedRoute });
      });
    },
  });
  return;
}

    this.mainService.add<any, any>('Equipment', dto).subscribe({
      next: (saved) => {
        const savedId = (saved as any)?.id ?? (saved as any)?.Id ?? 0;
        if (savedId) this.equipmentRequest.id = Number(savedId);

        Swal.fire({
          title: 'Gemt',
          text: 'Udstyret er oprettet.',
          icon: 'success',
          confirmButtonText: 'Ok',
        }).then(() => {
          this.router.navigate(['../søgudstyr'], { relativeTo: this.activatedRoute });
        });
      },
    });
  }


  deleteEntry() {
    if (!this.isEditMode || !this.equipmentRequest.id) return;

    Swal.fire({
      title: 'Slet udstyr?',
      text: 'Er du sikker på at du vil slette udstyret?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, slet',
      cancelButtonText: 'Annuller',
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.mainService.delete('Equipment', this.equipmentRequest.id).subscribe({
        next: () => {
          Swal.fire({
            title: 'Slettet',
            text: 'Udstyret er slettet.',
            icon: 'success',
            confirmButtonText: 'Ok',
          }).then(() => {
            this.router.navigate(['../../søgudstyr'], { relativeTo: this.activatedRoute });
          });
        },
      });
    });
  }
  private resetForm() {
    this.selectedCategoryId = 0;
    this.selectedSubCategoryId = 0;
    this.selectedDepartmentId = 0;
    this.selectedLocationId = 0;
    this.selectedResponsibleUserId = 0;
    this.nextControlText = '';
    this.qrImageSrc = null;
    this.equipmentImageSrc = null;

    this.equipmentRequest = {
      ...this.equipmentRequest,
      id: 0,
      name: '',
      model: '',
      series: '',
      comment: '',
      qrToken: '',
      supplierName: '',
      serviceFrequencyMonths: '',
      serviceFrequencyDelay: 0,
      purchaseDate: new Date(),
      placement: '',
      alarmRecipientEmail: '',
      supplierPhone: '',
      supplierAddress: '',
      bestbefore: new Date(),
      guideLink: '',
      responsibleId: 0,
      isServiceOK: IsServiceOkDTO.IkkeKontrolleret,
      departmentId: 0,
      locationId: 0,
      subCategoryId: 0,
      createdById: 0,
    };
  }
}
